import { Page, pickSeoProps } from '@components/Page'
import { RolexBreadcrumbs } from '@components/_rolex/Breadcrumbs'
import { RolexContainer } from '@components/_rolex/Container'
import { RolexImage } from '@components/_rolex/Image'
import RolexPage from '@components/_rolex/Page'
import { RolexPageIntro } from '@components/_rolex/PageIntro'
import { RolexPushBanner } from '@components/_rolex/push-banner-grid/PushBanner'
import KeepExploring from '@components/_rolex/sliders/KeepExploring'
import { buildLanguageAlternateNavigation } from '@lib/build-language-alternates'
import { i18nTranslationNamespaces } from '@lib/i18n-translation-files'
import { SingleItemResponse, getPageByCodename } from '@lib/kontent'
import { RolexPageIntro as RolexPageIntroModel } from '@lib/kontent/models/RolexPageIntro'
import { RolexWorldOfRolexArticle } from '@lib/kontent/models/RolexWorldOfRolexArticle'
import { RolexWorldOfRolexPage } from '@lib/kontent/models/RolexWorldOfRolexPage'
import { GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'

type WorldOfRolexPageProps = SingleItemResponse<RolexWorldOfRolexPage>

const WorldOfRolexPage: NextPage<WorldOfRolexPageProps> = ({ item, modular_content }) => {
  const { locales, locale } = useRouter()
  const { t } = useTranslation('rolex')
  const pageIntro = modular_content[item.elements.intro_section.value[0]]
    .elements as RolexPageIntroModel
  const featuredItem = modular_content[item.elements.featured_article.value[0]]
    .elements as RolexWorldOfRolexArticle
  const otherItems = item.elements.other_articles.value.map(
    (key) => modular_content[key].elements,
  ) as RolexWorldOfRolexArticle[]
  const keepExploringItems = item.elements.keep_exploring_slider_items.value.map(
    (key) => modular_content[key].elements,
  )
  return (
    <Page
      {...pickSeoProps(item.elements)}
      languageAlternates={buildLanguageAlternateNavigation(locales, 'rolex.worldOfRolex', '/rolex')}
      noindex={locale === 'de'}
    >
      <RolexPage pageType="article hub page">
        <RolexBreadcrumbs
          items={[]}
          currentPageTitle={t('navigation.worldOfRolex', { ns: 'rolex' })}
        />
        <div className="bg-rolexLightBeige">
          <RolexImage
            mobileImage={item.elements.mobile_image}
            desktopImage={item.elements.desktop_image}
            size="xl"
          />
          <RolexPageIntro
            title={pageIntro.title.value}
            description={pageIntro.description}
            titleTag={pageIntro.title_tag.value[0]?.codename || 'h1'}
          />
          <section className={'py-[30px] md:py-[45px] lg:py-[60px]'}>
            <RolexContainer as="div">
              <RolexPushBanner
                pretitle={featuredItem.publishing_date.value}
                title={featuredItem.title.value}
                buttonLink={`/rolex/${t('rolex.worldOfRolex', { ns: 'navigation' })}/${
                  featuredItem.url_slug.value
                }`}
                description={featuredItem.description.value}
                buttonText={t('learnMore')}
                mobileImage={featuredItem.mobile_image}
                desktopImage={featuredItem.desktop_image}
                gridLayout="n1_column"
              />
            </RolexContainer>
          </section>
          <section className={'py-[30px] md:py-[45px] lg:py-[60px]'}>
            <RolexContainer as="div">
              <div className={'grid grid-cols-1 gap-x-2 gap-y-12 md:grid-cols-2'}>
                {otherItems.map((item) => (
                  <RolexPushBanner
                    key={item.url_slug.value}
                    pretitle={item.publishing_date.value}
                    title={item.title.value}
                    buttonLink={`/rolex/${t('rolex.worldOfRolex', { ns: 'navigation' })}/${
                      item.url_slug.value
                    }`}
                    description={item.description.value}
                    buttonText={t('learnMore')}
                    mobileImage={item.mobile_image}
                    desktopImage={item.desktop_image}
                    gridLayout="n2_columns"
                  />
                ))}
              </div>
            </RolexContainer>
          </section>
        </div>
        <KeepExploring className="bg-white py-[60px] lg:py-[90px]" items={keepExploringItems} />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps<WorldOfRolexPageProps> = async ({
  locale,
  preview,
}) => {
  try {
    const language = ['sg', 'de'].includes(locale) ? 'en' : locale
    const content = await getPageByCodename<RolexWorldOfRolexPage>({
      codename: 'rolex___world_of_rolex',
      lang: language,
      preview,
    })

    return {
      props: {
        ...content,
        ...(await serverSideTranslations(language, i18nTranslationNamespaces)),
      },
    }
  } catch (error) {
    throw error
  }
}

export default WorldOfRolexPage
